import { Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

import { Accordion } from '@/components/Accordion/Accordion';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { EditTestamentaryEntitySection } from '@/modules/entities/testamentaryEntities/EditTestamentaryEntitySplitScreenModal/EditTestamentaryEntitySplitScreen.types';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { EstatePage_TestamentaryEntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';

import { EstateSubtrustCard } from './EstateSubtrustCard/EstateSubtrustCard';
import { groupSubtrustsByCreatedFrom } from './SubtrustsContainer.utils';

export interface SubtrustsContainerProps {
  subtrusts: EstatePage_TestamentaryEntityFragment[];
  onViewDetails: (subtrust: EstatePage_TestamentaryEntityFragment) => void;
  onAISuggestionsClick: (
    subtrust: EstatePage_TestamentaryEntityFragment,
    section: EditTestamentaryEntitySection
  ) => void;
}

interface SubtrustGroupsProps<Subtrust> {
  parentGroups: Array<{
    key: string;
    parentNames: string;
    subtrusts: Subtrust[];
  }>;
  allOthers: Subtrust[];
  renderSubtrusts: (subtrusts: Subtrust[]) => ReactNode;
}

export function SubtrustGroups<Subtrust>({
  parentGroups,
  allOthers,
  renderSubtrusts,
}: SubtrustGroupsProps<Subtrust>) {
  if (parentGroups.length === 0) {
    return renderSubtrusts(allOthers);
  }

  return (
    <Stack spacing={2.5}>
      {parentGroups.map((group) => (
        <Stack key={group.key} spacing={1}>
          <Stack spacing={0.25} minWidth={0}>
            <Typography variant="eyebrow-xs">Created from</Typography>
            <Typography variant="title-xs" noWrap>
              {group.parentNames}
            </Typography>
          </Stack>
          {renderSubtrusts(group.subtrusts)}
        </Stack>
      ))}
      {allOthers.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="title-xs">All others</Typography>
          {renderSubtrusts(allOthers)}
        </Stack>
      )}
    </Stack>
  );
}

export function SubtrustsContainer({
  subtrusts,
  onViewDetails,
  onAISuggestionsClick,
}: SubtrustsContainerProps) {
  const theme = useTheme();
  const canEdit = useCanEditHouseholdData();
  const { unacknowledgedSuggestionsV2 } = useEstateHoldingsContext();

  if (!subtrusts || subtrusts.length === 0) {
    return null;
  }

  const { parentGroups, allOthers } = groupSubtrustsByCreatedFrom(subtrusts);

  const renderSubtrustCards = (
    subtrustsToRender: EstatePage_TestamentaryEntityFragment[]
  ) => (
    <Stack spacing={1}>
      {subtrustsToRender.map((subtrust) => (
        <EstateSubtrustCard
          key={subtrust.id}
          subtrust={subtrust}
          entitySuggestionsV2={unacknowledgedSuggestionsV2[subtrust.id]}
          canEdit={canEdit}
          onAISuggestionsClick={onAISuggestionsClick}
          onViewSubtrustDetails={onViewDetails}
        />
      ))}
    </Stack>
  );

  return (
    <Accordion
      data-testid="estate-subtrusts-container"
      variant="filled"
      withDivider={false}
      isDefaultExpanded={true}
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="title-s">Sub-trusts</Typography>
          <ContextualHelpTooltip>
            <PopperContent
              sx={{
                maxWidth: theme.spacing(50),
              }}
              body={'Trusts that become active upon the death of the grantor.'}
            />
          </ContextualHelpTooltip>
        </Stack>
      }
    >
      <SubtrustGroups
        parentGroups={parentGroups}
        allOthers={allOthers}
        renderSubtrusts={renderSubtrustCards}
      />
    </Accordion>
  );
}
